import { API_BASE, apiFetch } from "../lib/api";
import { HierarchyAccessError, id, loadHierarchy, object, request, session } from "./api";
import { browserPersistence, type SendPersistence } from "./send";
import type { TranscriptSelection } from "./transcript";
import { forkEligibility } from "./fork";

export interface CreateSelection { accountId: string; workspaceId: string; projectId?: string; sourceThreadId?: string; nodeId?: string }
interface CreateInput { id: string; name: string; projectId?: string; nodeId?: string }
export interface CreationSnapshot { scope: string[]; name: string; pending?: CreateInput; created?: CreateInput; unavailableCreatedId?: string }

function name(value: unknown): string {
  if (typeof value !== "string" || !value.trim() || new TextEncoder().encode(value).length > 512 || value.includes("\0")) throw new HierarchyAccessError();
  return value;
}
function input(raw: unknown, projectId?: string, nodeId?: string): CreateInput {
  const value = object(raw);
  if (Object.keys(value).some((key) => !["id", "name", "projectId", "nodeId"].includes(key)) || value.projectId !== projectId || value.nodeId !== nodeId) throw new HierarchyAccessError();
  return { id: id(value.id), name: name(value.name), projectId, nodeId };
}

async function scopedCreator(selection: CreateSelection, userId: string, persistence?: SendPersistence): Promise<ThreadCreator> {
  for (const value of Object.values(selection)) if (value !== undefined) id(value);
  id(userId);
  if (Boolean(selection.sourceThreadId) !== Boolean(selection.nodeId)) throw new HierarchyAccessError();
  const scope = [API_BASE, userId, selection.accountId, selection.workspaceId, selection.projectId ?? ""];
  if (selection.sourceThreadId) scope.push("fork", selection.sourceThreadId, selection.nodeId!);
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(scope)));
  const key = "chill.thread-create.v1." + Array.from(new Uint8Array(digest), (n) => n.toString(16).padStart(2, "0")).join("");
  return new ThreadCreator(selection, scope, key, persistence ?? browserPersistence());
}

// Local presentation hint only. No names or grants escape; callers must have a
// currently authorized page identity, and opening the creator reauthorizes again.
export async function hasLocalForkRecovery(selection: CreateSelection, userId: string, persistence?: SendPersistence): Promise<boolean> {
  if (!selection.sourceThreadId || !selection.nodeId) return false;
  return (await scopedCreator(selection, userId, persistence)).hasRecovery();
}

export async function openThreadCreator(selection: CreateSelection, persistence?: SendPersistence, signal?: AbortSignal): Promise<ThreadCreator> {
  const identity = await session(signal);
  if (identity.accountId !== selection.accountId) throw new HierarchyAccessError();
  const creator = await scopedCreator(selection, identity.userId, persistence);
  await creator.read(signal);
  return creator;
}

export class ThreadCreator {
  constructor(private readonly selection: CreateSelection, private readonly scope: string[], private readonly key: string, private readonly persistence: SendPersistence) {}
  private async authorize(signal?: AbortSignal) {
    const catalog = await loadHierarchy(this.selection, signal);
    if (catalog.scope.userId !== this.scope[1] || catalog.scope.accountId !== this.scope[2] || !catalog.canCreateThread) throw new HierarchyAccessError();
    if (this.selection.sourceThreadId) {
      const path = `/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}/threads/${encodeURIComponent(this.selection.sourceThreadId)}`;
      const source = await request(path, signal);
      if (source.id !== this.selection.sourceThreadId || source.accountId !== this.selection.accountId || source.workspaceId !== this.selection.workspaceId ||
        (source.projectId || undefined) !== this.selection.projectId || source.role !== "coder" || !["admin","editor"].includes(String(source.accessRole))) throw new HierarchyAccessError();
      const turn = await request(`${path}/turns/${encodeURIComponent(this.selection.nodeId!)}`, signal);
      const scope = object(turn.scope);
      if (turn.id !== this.selection.nodeId || scope.run_id !== this.selection.nodeId || scope.account_id !== this.selection.accountId || scope.workspace_id !== this.selection.workspaceId) throw new HierarchyAccessError();
      const identity = await session(signal);
      if (identity.userId !== this.scope[1] || identity.accountId !== this.scope[2]) throw new HierarchyAccessError();
    }
  }
  private async eligible(signal?: AbortSignal) {
    if (this.selection.sourceThreadId && !(await forkEligibility({ ...this.selection, threadId: this.selection.sourceThreadId }, this.selection.nodeId!, this.scope[1], signal))) throw new HierarchyAccessError();
  }
  private saved(): CreationSnapshot {
    const raw = this.persistence.getItem(this.key);
    if (raw === null) return { scope: this.scope, name: "" };
    const value = object(JSON.parse(raw));
    if (Object.keys(value).some((key) => !["scope", "name", "pending", "created"].includes(key)) || JSON.stringify(value.scope) !== JSON.stringify(this.scope) ||
      typeof value.name !== "string" || (value.pending !== undefined && value.created !== undefined)) throw new HierarchyAccessError();
    return { scope: this.scope, name: value.name,
      pending: value.pending === undefined ? undefined : input(value.pending, this.selection.projectId, this.selection.nodeId),
      created: value.created === undefined ? undefined : input(value.created, this.selection.projectId, this.selection.nodeId) };
  }
  hasRecovery(): boolean { const value = this.saved(); return Boolean(value.pending || value.created); }
  private persist(value: CreationSnapshot) { this.persistence.setItem(this.key, JSON.stringify(value)); }
  private validate(raw: unknown, expected: CreateInput) {
    const value = object(raw);
    if (value.id !== expected.id || value.accountId !== this.selection.accountId || value.workspaceId !== this.selection.workspaceId ||
      (value.projectId || undefined) !== this.selection.projectId) throw new HierarchyAccessError();
    if (this.selection.sourceThreadId && (value.forkedFromThreadId !== this.selection.sourceThreadId || value.forkedFromNodeId !== this.selection.nodeId)) throw new HierarchyAccessError();
  }
  target(value: CreationSnapshot): TranscriptSelection {
    if (!value.created) throw new HierarchyAccessError();
    const {accountId, workspaceId, projectId} = this.selection;
    return { accountId, workspaceId, projectId, threadId: value.created.id };
  }
  async read(signal?: AbortSignal): Promise<CreationSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const value = this.saved();
      if (!value.pending && !value.created) await this.eligible(signal);
      if (value.created) {
        const response = await apiFetch(`/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}/threads/${encodeURIComponent(value.created.id)}`, { credentials: "include", cache: "no-store", signal });
        if (response.status === 403 || response.status === 404) {
          await response.body?.cancel();
          await this.authorize(signal); signal?.throwIfAborted();
          // Retain the durable receipt, but disclose no old private name. The
          // person can explicitly start another conversation in this scope.
          return { scope: this.scope, name: "", unavailableCreatedId: value.created.id };
        }
        if (!response.ok) throw new HierarchyAccessError();
        this.validate(await response.json(), value.created);
        await this.authorize(signal);
      }
      signal?.throwIfAborted();
      return value;
    });
  }
  async saveName(text: string, signal?: AbortSignal): Promise<CreationSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      const value = this.saved();
      if (value.pending || value.created) throw new HierarchyAccessError();
      value.name = text;
      signal?.throwIfAborted(); this.persist(value); return value;
    });
  }
  async create(expectedName: string, signal?: AbortSignal): Promise<CreationSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const value = this.saved();
      if (value.created) {
        if (value.created.name !== expectedName) throw new HierarchyAccessError();
        this.validate(await request(`/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}/threads/${encodeURIComponent(value.created.id)}`, signal), value.created);
        await this.authorize(signal); signal?.throwIfAborted(); return value;
      }
      if (value.name !== expectedName || (value.pending && value.pending.name !== expectedName)) throw new Error("The saved conversation changed. Reload it before continuing.");
      if (!value.pending) {
        await this.eligible(signal);
        value.pending = { id: crypto.randomUUID(), name: name(expectedName), projectId: this.selection.projectId, nodeId: this.selection.nodeId };
      }
      signal?.throwIfAborted(); this.persist(value);
      const base = `/code/workspaces/${encodeURIComponent(this.selection.workspaceId)}/threads`;
      const path = this.selection.sourceThreadId ? `${base}/${encodeURIComponent(this.selection.sourceThreadId)}/forks` : base;
      const body = this.selection.sourceThreadId ? {id:value.pending.id, name:value.pending.name, nodeId:value.pending.nodeId} : value.pending;
      const response = await apiFetch(path, {
        method: "POST", credentials: "include", cache: "no-store", signal,
        headers: { "content-type": "application/json" }, body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error("Creation couldn't be confirmed. Retry the saved conversation.");
      this.validate(await response.json(), value.pending);
      await this.authorize(signal); signal?.throwIfAborted();
      value.created = value.pending; value.pending = undefined;
      this.persist(value); return value;
    });
  }
  async startAnother(expectedCreatedId: string, signal?: AbortSignal): Promise<CreationSnapshot> {
    return this.persistence.withLock(this.key, signal, async () => {
      await this.authorize(signal);
      const value = this.saved();
      if (value.created?.id !== expectedCreatedId || value.pending) throw new HierarchyAccessError();
      const fresh = { scope: this.scope, name: "" };
      signal?.throwIfAborted(); this.persist(fresh); return fresh;
    });
  }
}
